import React, { FC } from "react"
import { useSelector } from "react-redux"
import { getHasCostShare } from "../../../../derivedSelectors"
import {
  CSD_CONFIGURATION,
  COMPANY_DEDUCTIBLE_STATUS,
  useUserDeductible
} from "../../../context/user/DeductibleContext"
import InformationModal from "../../modal/InformationModal"
import { CSDTableContainer } from "./CSDTable"
import { Spacer } from "../../atoms/Atoms"
import { H2Library, Body, Container } from "@carrotfertility/carotene"
import { ExternalDeductibleInformational } from "./ExternalDeductibleInformational"
import L10n from "../../../../lib/carrot-l10n"
import { YourCostShareInformational } from "./YourCostShareInformational"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

export type HowMuchMemberPartnerReimbursedModalProps = {
  onExit: () => void
}

const HowMuchMemberPartnerReimbursedModal: FC<HowMuchMemberPartnerReimbursedModalProps> = ({ onExit }) => {
  const costShare = useSelector(getHasCostShare)
  const { companyDeductibleStatus, csdConfiguration, hasCSD } = useUserDeductible()
  return (
    <InformationModal onExit={onExit} titleText={l`How Much Will I Be Reimbursed?`}>
      <div className="ph45 mw-600">
        <ReimbursementModalHeaderAndCopy
          deductibleStatus={companyDeductibleStatus}
          csdConfig={csdConfiguration}
          hasCSD={hasCSD}
        />
        {companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.CARROT_SPECIFIC_DEDUCTIBLE && <CSDTableContainer />}
        {companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.EXTERNAL && <ExternalDeductibleInformational />}
        {costShare && <YourCostShareInformational />}
        <Spacer height={3.0} />
      </div>
    </InformationModal>
  )
}

export type ReimbursementModalHeaderAndCopyProps = {
  csdConfig: string
  deductibleStatus: string
  hasCSD: boolean
}

function ReimbursementModalHeaderAndCopy({
  csdConfig,
  deductibleStatus,
  hasCSD
}: ReimbursementModalHeaderAndCopyProps): JSX.Element {
  const notCSDEnabled = deductibleStatus !== COMPANY_DEDUCTIBLE_STATUS.CARROT_SPECIFIC_DEDUCTIBLE
  const csdEnabled = deductibleStatus === COMPANY_DEDUCTIBLE_STATUS.CARROT_SPECIFIC_DEDUCTIBLE
  const memberAndPartnerSeparateCSD = csdEnabled && csdConfig === CSD_CONFIGURATION.MEMBER_AND_PARTNER_SEPARATE
  const hasLegacyCSD = csdConfig === null && hasCSD
  const memberOnlyCSD = csdEnabled && (csdConfig === CSD_CONFIGURATION.MEMBER_ONLY || hasLegacyCSD)
  const memberAndPartnerCombinedCSD = csdEnabled && csdConfig === CSD_CONFIGURATION.MEMBER_AND_PARTNER_COMBINED
  const partnerOnlyCSD = csdEnabled && csdConfig === CSD_CONFIGURATION.PARTNER_ONLY
  return (
    <>
      {notCSDEnabled && (
        <Container padding="none">
          <H2Library stack="large">{l`How much will I be reimbursed?`}</H2Library>
          <Body color="black-80">
            {l`Individual reimbursements are subject to bank fees, taxes, and other plan details like deductibles and cost sharing. Here’s what applies to you:`}
          </Body>
        </Container>
      )}

      {memberAndPartnerSeparateCSD && (
        <Container padding="none" stack="large">
          <H2Library stack="large">{l`How much will my partner and I be reimbursed?`}</H2Library>
          <Body color="black-80">
            {l`You and your partner will need to meet annual deductibles before you can be reimbursed for qualified medical expenses. Reimbursements may also be subject to bank fees, taxes, and other plan details.`}
          </Body>
        </Container>
      )}

      {(memberOnlyCSD || memberAndPartnerCombinedCSD) && (
        <Container padding="none" stack="large">
          <H2Library stack="large">{l`How much will I be reimbursed?`}</H2Library>
          <Body color="black-80">
            {l`You’ll need to meet an annual deductible before you can be reimbursed for qualified medical expenses. Reimbursements may also be subject to bank fees, taxes, and other plan details.`}
          </Body>
        </Container>
      )}

      {partnerOnlyCSD && (
        <Container padding="none" stack="large">
          <H2Library stack="large">{l`How much will my partner and I be reimbursed?`}</H2Library>
          <Body color="black-80">
            {l`Your partner will need to meet an annual deductible before you can be reimbursed for their qualified medical expenses. Reimbursements may also be subject to bank fees, taxes, and other plan details.`}
          </Body>
        </Container>
      )}
    </>
  )
}

export { HowMuchMemberPartnerReimbursedModal }
